import { logUsage } from 'owa-analytics';
import { launchCalendarFullCompose } from 'owa-calendar-forms-launch-handlers';
import { getActionSourceFromCommandingViewMode } from 'owa-command-ribbon';
import { lazyShowInsertLinkDialog } from 'owa-compose-ribbon';
import { owaDate } from 'owa-datetime';
import { InsertLinkSource } from 'owa-editor-ribbonplugin/lib/types/InsertLinkSource';
import isFeatureEnabled from 'owa-feature-flags/lib/utils/isFeatureEnabled';
import initializeYammerPublisher from 'owa-mail-commands/lib/actions/initializeYammerPublisher';
import onArchive from 'owa-mail-commands/lib/actions/onArchive';
import onBlock from 'owa-mail-commands/lib/actions/onBlock';
import onClearMsgPermissions from 'owa-mail-commands/lib/actions/onClearMsgPermissions';
import onCreateRule from 'owa-mail-commands/lib/actions/onCreateRule';
import onDelete from 'owa-mail-commands/lib/actions/onDelete';
import { onFlagUnflag } from 'owa-mail-commands/lib/actions/onFlagUnflag';
import onIgnoreStopIgnore from 'owa-mail-commands/lib/actions/onIgnoreStopIgnore';
import onJunkNotJunk from 'owa-mail-commands/lib/actions/onJunkNotJunk';
import onMarkAsPhishing from 'owa-mail-commands/lib/actions/onMarkAsPhishing';
import onMarkAsReadUnread from 'owa-mail-commands/lib/actions/onMarkAsReadUnread';
import onNewYammerPost from 'owa-mail-commands/lib/actions/onNewYammerPost';
import onPinUnpin from 'owa-mail-commands/lib/actions/onPinUnpin';
import onPrint from 'owa-mail-commands/lib/actions/onPrint';
import onSensitivity from 'owa-mail-commands/lib/actions/onSensitivity';
import onShowInImmersiveReader from 'owa-mail-commands/lib/actions/onShowInImmersiveReader';
import onSnooze from 'owa-mail-commands/lib/actions/onSnooze';
import onSweep from 'owa-mail-commands/lib/actions/onSweep';
import onToggleMessageClassification from 'owa-mail-commands/lib/actions/onToggleMessageClassification';
import onToggleRmsTemplate from 'owa-mail-commands/lib/actions/onToggleRmsTemplate';
import onUndo from 'owa-mail-commands/lib/actions/onUndo';
import { save } from 'owa-mail-compose-command';
import { getMailMenuItemShouldShowMap, MenuItemType } from 'owa-mail-filterable-menu-behavior';
import { lazyToggleFolderPaneExpansion } from 'owa-mail-layout';
import checkAndCreateNewMessage from 'owa-mail-message-actions/lib/actions/checkAndCreateNewMessage';
import { lazyOnOpenCustomizer } from 'owa-mail-ribbon-customizer';
import { lazyCreateNewNote } from 'owa-notes-store';
import { RecipientType } from 'owa-recipient-types/lib/types/RecipientType';
import RecipientWellWithFindControlViewState from 'owa-recipient-types/lib/types/RecipientWellWithFindControlViewState';
import { lazySelfMountGALDialog } from 'owa-recipient-well-label';
import type { OwaDate } from 'owa-datetime';
import type EditorViewState from 'owa-editor/lib/store/schema/EditorViewState';
import {
    onReply,
    onReplyAll,
    onReplyWithMeeting,
    onForward,
} from 'owa-mail-commands/lib/actions/onResponse';
import type FlagType from 'owa-service/lib/contract/FlagType';
import type { ComposeViewState } from 'owa-mail-compose-store';
import type CLPLabel from 'owa-mail-protection-types/lib/schema/CLPLabel';
import type MessageClassificationType from 'owa-service/lib/contract/MessageClassificationType';
import { lazyToggleExpressionPane } from 'owa-expressions-view';
import { RIBBON_OPENED_SOURCE } from 'owa-expressions-store/lib/store/schema/ExpressionPickerOpenedSource';
import isExpressionPaneOpen from 'owa-expressions-store/lib/utils/isExpressionPaneOpen';
import type AttachmentMultiPicker from 'owa-mail-compose-view/lib/types/AttachmentMultiPicker';

export const onNewEvent = () => {
    launchCalendarFullCompose({
        fullComposeEntrySource: 'Mail_NewItems_EventButton',
    });
};

export const onToggleFolderPane = () => {
    lazyToggleFolderPaneExpansion.importAndExecute();
};

export const onNewMessage = () => {
    checkAndCreateNewMessage(getActionSourceFromCommandingViewMode(), null /* targetId */);
};

export const onShowInImmersiveReaderClicked = () => {
    onShowInImmersiveReader(getActionSourceFromCommandingViewMode());
};

export const onPrintClicked = () => {
    onPrint(getActionSourceFromCommandingViewMode());
};

export const onDeleteClicked = () => {
    onDelete(
        getActionSourceFromCommandingViewMode() /* actionSourceMailStore */,
        getActionSourceFromCommandingViewMode() /* actionSourceAnalyticsActions */
    );
};

export const onArchiveClicked = () => {
    onArchive(getActionSourceFromCommandingViewMode());
};

export const onMarkAsReadUnreadClicked = () => {
    onMarkAsReadUnread(getActionSourceFromCommandingViewMode());
};

export const onReplyWithMeetingClicked = () => {
    onReplyWithMeeting(getActionSourceFromCommandingViewMode());
};

export const onPinUnpinClicked = () => {
    onPinUnpin(getActionSourceFromCommandingViewMode());
};

export const onSaveDraft = (viewState: ComposeViewState) => {
    // Other methods of save have their own logging pipeline with which it would be difficult to merge
    logUsage('SaveDraft', {
        actionSource: getActionSourceFromCommandingViewMode(),
    });
    save(viewState);
};

export const onSensitivityClicked = (
    label: CLPLabel,
    viewState: ComposeViewState,
    targetWindow: Window
) => {
    onSensitivity(viewState, label, targetWindow);
};

export const onSweepClicked = () => {
    onSweep();
};

export const onSnoozeClicked = (date?: OwaDate) => {
    onSnooze(date, getActionSourceFromCommandingViewMode());
};

export const onFlagUnflagClicked = (flagType?: FlagType, override?: boolean) => () => {
    onFlagUnflag(getActionSourceFromCommandingViewMode(), flagType, override);
};

export const onReplyClicked = () => {
    onReply(getActionSourceFromCommandingViewMode());
};

export const onReplyAllClicked = () => {
    onReplyAll(getActionSourceFromCommandingViewMode());
};

export const onForwardClicked = () => {
    onForward(getActionSourceFromCommandingViewMode());
};

export const onUndoClicked = () => {
    onUndo(getActionSourceFromCommandingViewMode());
};

export const onIgnoreStopIgnoreClicked = () => {
    const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();
    onIgnoreStopIgnore(
        getActionSourceFromCommandingViewMode(),
        shouldShowBehaviorMap[MenuItemType.Ignore]()
    );
};

export const onJunkNotJunkClicked = () => {
    const shouldShowBehaviorMap = getMailMenuItemShouldShowMap();
    onJunkNotJunk(
        getActionSourceFromCommandingViewMode(),
        shouldShowBehaviorMap[MenuItemType.MarkAsJunk]()
    );
};

export const onMarkAsPhishingClicked = () => {
    onMarkAsPhishing(getActionSourceFromCommandingViewMode());
};

export const onBlockClicked = () => {
    onBlock(getActionSourceFromCommandingViewMode());
};

export const onCreateRuleClicked = () => {
    onCreateRule(getActionSourceFromCommandingViewMode());
};

export const onClearMsgPermissionsClicked = (viewState: ComposeViewState) => {
    onClearMsgPermissions(viewState);
};

export const onNewEmailSplitButtonExecuted = () => {
    if (isFeatureEnabled('tri-newYammerPostButton')) {
        initializeYammerPublisher();
        logUsage('YammerPublisher_MenuItemSeen');
    }
};

export const onNewYammerPostClicked = () => {
    onNewYammerPost();
};

export const onToggleMessageClassificationClicked = (
    viewState: ComposeViewState,
    classification: MessageClassificationType
) => {
    onToggleMessageClassification(viewState, classification);
};

export const onToggleRmsTemplateClicked = (
    viewState: ComposeViewState,
    RmsTemplateId: string,
    targetWindow: Window
) => {
    onToggleRmsTemplate(viewState, RmsTemplateId, targetWindow);
};

export const onRibbonCustomizerClicked = () => {
    lazyOnOpenCustomizer.importAndExecute();
};
export const onShowLinkDialogClicked = (viewState: ComposeViewState, targetWindow: Window) => {
    lazyShowInsertLinkDialog
        .import()
        .then(showInsertLinkDialog =>
            showInsertLinkDialog(
                viewState as EditorViewState,
                targetWindow,
                InsertLinkSource.OriginalRibbon,
                owaDate()
            )
        );
};

export const onEmojisButtonClicked = (
    viewState: ComposeViewState | undefined,
    targetWindow: Window
) => {
    if (viewState) {
        const shouldShowExpressionPane = !isExpressionPaneOpen(viewState.expressionId!);
        lazyToggleExpressionPane.import().then(toggleExpressionPane => {
            toggleExpressionPane(
                shouldShowExpressionPane,
                true /*useFlexPane*/,
                RIBBON_OPENED_SOURCE,
                viewState,
                viewState.expressionId,
                targetWindow
            );
        });
    }
};

export const onPicturesButtonClicked = (attachmentPickers: AttachmentMultiPicker) => {
    if (attachmentPickers.computer.current.current) {
        attachmentPickers.computer.current.current.click();
    }
};

export const onNewNoteClicked = () => {
    lazyCreateNewNote.importAndExecute('NotesFolder');
};

export const onAddressButtonClicked = (
    viewState: RecipientWellWithFindControlViewState,
    targetWindow: Window
) => {
    let recipientType: RecipientType = RecipientType.To; // TODO: Once the new design of the GAL pop up we should figure out how to address the To and Cc options

    lazySelfMountGALDialog
        .import()
        .then(selfMountGALDialog => selfMountGALDialog({ viewState, recipientType, targetWindow }));
};
